import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Table from 'rsuite/Table'
import Tag from 'rsuite/Tag'
import Toggle from 'rsuite/Toggle'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'

import Edit2 from '@rsuite/icons/Edit'
import TrashIcon from '@rsuite/icons/Trash'
import { toast } from 'react-toastify'
import { SortType } from 'rsuite-table/lib/@types/common'
import Pagination from 'rsuite/Pagination'
import IconButton from 'rsuite/esm/IconButton'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'

import { ItemType, StateParamsModel } from 'src/app/modules/common/Model'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import * as benefitMaster from '../index'

import { isEmpty } from 'lodash'
import DeleteModal from 'src/app/modules/common/components/deleteModal'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import AddBenefitMaster from './AddBenefitMaster'

const BenefitMasterSettings = () => {
  const dispatch = useDispatch()
  const [open, setOpen] = useState(false)
  const [actionType, setActionType] = useState('Add')
  const [params, setParams] = useState<StateParamsModel>({
    page: 1,
    limit: 10,
    orderBy: '',
  })
  const [checkedValues, setCheckedValues] = useState<Array<string>>([])
  const [editSelectedData, setEditCheckedData] = useState<any>()
  const [selectedData, setSelectedData] = useState<Array<string>>([])

  const [openSortModal, setOpenSortModal] = useState(false)
  const [alertOpen, setAlertOpen] = useState(false)
  const handleAlertOpen = () => setAlertOpen(true)
  const handleAlertClose = () => setAlertOpen(false)

  const BenefitMasterData: any = useSelector((state: any) => state.benefitMaster)
  const {
    activateSuccess,
    deactivateSuccess,
    singleActivateSuccess,
    singleDeactivateSuccess,
    success,
    loading,
    sortBenefitMasterData,
  } = BenefitMasterData

  useEffect(() => {
    dispatch(benefitMaster.actions.getBenefitMaster(params))
  }, [params])

  const handleChangeLimit = (dataKey: number) => {
    setParams({
      ...params,
      limit: dataKey,
    })
  }
  const handleToggleAction = (data: {[key: string]: string}) => {
    const formData = {id: data?.id}
    data?.status
      ? dispatch(benefitMaster.actions.singleDeactivateBenefitMaster(formData))
      : dispatch(benefitMaster.actions.singleActivateBenefitMaster(formData))
  }

  const ActionCell = ({rowData, dataKey, ...props}: any) => {
    return (
      <Cell {...props} className='link-group'>
        <CheckPermissions type='Edit'>
          <Whisper placement='top' trigger='hover' speaker={<Tooltip>Edit</Tooltip>}>
            <IconButton
              appearance='subtle'
              onClick={() => {
                setEditCheckedData(rowData)
                setActionType('Edit')
                setOpen(true)
              }}
              data-cy='edit-button'
              icon={<Edit2 />}
            />
          </Whisper>
        </CheckPermissions>
        <CheckPermissions type='Delete'>
          <Whisper placement='top' trigger='hover' speaker={<Tooltip>Delete</Tooltip>}>
            <IconButton
              appearance='subtle'
              onClick={() => {
                handleAlertOpen()
                let keySettingId = [rowData.id]
                setCheckedValues(keySettingId)
              }}
              data-cy='delete-button'
              icon={<TrashIcon />}
            />
          </Whisper>
        </CheckPermissions>

        <Whisper placement='top' trigger='hover' speaker={<Tooltip>Status</Tooltip>}>
          <Toggle
            size='sm'
            checked={rowData.status}
            onClick={() => handleToggleAction(rowData)}
            data-cy='toggle-button'
          />
        </Whisper>
      </Cell>
    )
  }

  const Cell = Table.Cell

  const handleClose = () => setOpen(false)

  const columns = [
    {
      label: 'S.N.',
      dataKey: 'sn',
      width: 60,
      cell: <Cell dataKey='sn' />,
    },
    {
      label: 'Title(E.N)',
      dataKey: 'benefitName',
      flexGrow: 1,
      cell: <Cell dataKey='benefitName' />,
      sortable: true,
    },
    {
      label: 'Title(N.P)',
      dataKey: 'benefitNameNp',
      flexGrow: 1,
      cell: <Cell dataKey='benefitNameNp' />,
      sortable: false,
    },
    {
      label: 'Beneficial Name(E.N)',
      dataKey: 'beneficialName',
      flexGrow: 1,
      cell: <Cell dataKey='beneficialName' />,
      sortable: true,
    },
    {
      label: 'Beneficial Name(N.P)',
      dataKey: 'beneficialNameNp',
      flexGrow: 1,
      cell: <Cell dataKey='beneficialNameNp' />,
      sortable: false,
    },
    {
      label: 'Sub Category',
      dataKey: 'benefitSubCategory',
      flexGrow: 1,
      cell: <Cell dataKey='benefitSubCategory' />,
      sortable: false,
    },
    {
      label: 'Status',
      dataKey: 'status',
      width: 85,
      cell: (
        <Cell>
          {(rowData) =>
            rowData?.status ? (
              <Tag color='green' size='sm'>
                Active
              </Tag>
            ) : (
              <Tag color='red' size='sm'>
                Inactive
              </Tag>
            )
          }
        </Cell>
      ),
      sortable: false,
    },

    {
      label: 'Action',
      width: 150,
      align: 'center',
      cell: <ActionCell dataKey='id' />,
    },
  ]

  useEffect(() => {
    if (BenefitMasterData?.deleteSuccess) {
      toast.success('Benefit Master deleted successfully')
      dispatch(benefitMaster?.actions.getBenefitMaster(params))
    }
  }, [BenefitMasterData])

  useEffect(() => {
    if (singleActivateSuccess) {
      toast.success('Benefit Master activated successfully')
    }
    if (singleDeactivateSuccess) {
      toast.success('Benefit Master deactivated successfully')
    }
    handleChecked([])
  }, [singleActivateSuccess, singleDeactivateSuccess])

  useEffect(() => {
    if (activateSuccess) {
      toast.success('Benefit Master activated successfully')
    }
    if (deactivateSuccess) {
      toast.success('Benefit Master deactivated successfully')
    }
    handleChecked([])
    dispatch(benefitMaster?.actions.getBenefitMaster(params))
  }, [activateSuccess, deactivateSuccess])

  const handleCheck = (data: any) => {
    setSelectedData(data)
  }

  const handleSort = (dataKey: string, sortType?: SortType) => {
    setParams({...params, orderBy: dataKey, order: sortType})
  }

  const activateSelectedData = () => {
    if (!isEmpty(checkedValues)) {
      const formData: any = checkedValues.map((d: string) => ({
        id: d,
      }))
      dispatch(benefitMaster.actions.activateBenefitMaster(formData))
    } else {
      toast.error('No data Selected')
    }
  }

  const deactivateSelectedData = () => {
    if (!isEmpty(checkedValues)) {
      const formData: any = checkedValues.map((d: string) => ({
        id: d,
      }))
      dispatch(benefitMaster.actions.deactivateBenefitMaster(formData))
    } else {
      toast.error('No data Selected')
    }
  }

  const handleChecked = (values: any) => {
    setCheckedValues(values)
  }

  const data = BenefitMasterData?.data?.benefitMaster
    ? BenefitMasterData?.data?.benefitMaster?.map((item: any, i: number) => ({
        sn: (params?.page - 1) * params?.limit + (i + 1),
        id: item?.id,
        benefitSubCategoryId: item.benefitSubCategory?.id,
        benefitSubCategory: item.benefitSubCategory?.name,
        benefitName: item.benefitName,
        name: item?.benefitName,
        benefitNameNp: item.benefitNameNp,
        beneficialName: item.beneficialName,
        beneficialNameNp: item.beneficialNameNp,
        status: item.status,
      }))
    : []

  const handleAddModal = () => {
    setEditCheckedData({})
    setOpen(true)
    setActionType('Add')
  }

  const modalSubmit = () => {
    let id = checkedValues?.map((value) => ({id: value}))
    dispatch(benefitMaster?.actions?.deleteBenefitMaster(id))
    handleAlertClose()
  }

  const handleRefresh = () => {
    dispatch(benefitMaster?.actions.getBenefitMaster(params))
  }
  const handleGetAllData = () => {
    setParams({...params, limit: BenefitMasterData?.data?.meta?.total})
    dispatch(benefitMaster?.actions.getBenefitMaster(params))
  }

  const handleSubmitSort = (data: Array<ItemType>) => {
    let body = {
      benefitMasterId: data?.map((item) => ({id: item?.keyId})),
    }
    dispatch(benefitMaster?.actions.sortBenefitMaster(body))
  }

  useEffect(() => {
    if (!isEmpty(sortBenefitMasterData) && success) {
      toast.success('Benefit Master sorted successfully')
      dispatch(benefitMaster.actions.sortBenefitMasterReset())
      setParams({...params, limit: 10})
      dispatch(benefitMaster.actions.getBenefitMaster(params))
      setOpenSortModal(false)
    }
  }, [sortBenefitMasterData, success])

  return (
    <>
      <div className='shadow p-3 bg-white rounded'>
        <DesignComponent
          moduleName='Benefit Master Settings'
          params={params}
          setParams={setParams}
          handleRefresh={handleRefresh}
          handleAddModal={handleAddModal}
          handleAlertOpen={handleAlertOpen}
          exportShow={false}
          importShow={false}
          enableMultiple={activateSelectedData}
          disableMultiple={deactivateSelectedData}
          data={data}
          sortShow={true}
          sortButtonName='Sort'
          handleSubmitSort={handleSubmitSort}
          handleGetAllData={handleGetAllData}
          openSortModal={openSortModal}
          setOpenSortModal={setOpenSortModal}
          loading={loading}
        />

        <div className='datatable'>
          <RSuiteTable
            onChecked={handleChecked}
            showCheckbox={true}
            columns={columns}
            data={data}
            showLoading={BenefitMasterData?.loading}
            checkedValues={checkedValues}
            handleSort={handleSort}
          />
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            className='mt-5'
            maxButtons={5}
            size='sm'
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={BenefitMasterData?.data?.meta?.total || 0}
            limitOptions={[10, 20, 50, 100]}
            limit={params?.limit}
            activePage={params?.page}
            onChangePage={(value) => setParams({...params, page: value})}
            onChangeLimit={handleChangeLimit}
          />
          {open && (
            <AddBenefitMaster
              open={open}
              params={params}
              handleClose={handleClose}
              actionType={actionType}
              editSelectedData={editSelectedData}
            />
          )}
        </div>
        {alertOpen &&
          (!isEmpty(checkedValues) ? (
            <DeleteModal
              handleClick={() => modalSubmit()}
              isOpen={alertOpen}
              handleClose={() => handleAlertClose()}
            />
          ) : (
            toast.error('No data selected') && setAlertOpen(false)
          ))}
      </div>
    </>
  )
}

export default BenefitMasterSettings
